// ─── Service « Carnet de contacts partagé » ───────────────────────
// Répertoire d'un calendrier (médecin, école, personnes de confiance, urgences…).
// Collection `contacts/{id}` : { calendrierId, prenom?, nom?, surnom?, role,
//   telephones[], emails[], lieux[], note, creePar, creeLe }.
//   telephones/emails : [{ id, type, valeur, principal }] — plusieurs valeurs
//     typées (mobile/domicile/travail…), une marquée « principale ».
//   lieux : [{ lieuId, principal }] — références vers les LIEUX du carnet (pas
//     d'adresse copiée) ; l'adresse/carte se résolvent en direct depuis le lieu.
// Rétro-compat : anciens champs scalaires telephone/email/lieuId/adresse relus.
// Règles : accesCalId (participant MoPCal OU membre d'espace) — cf. mop-cal-v3.
import { db } from '@mop/core';
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot, query, where } from 'firebase/firestore';

// Catégories de contact (emoji, style suite).
export const ROLES_CONTACT = [
  { id: 'medecin', label: 'Médecin',       emoji: '🩺' },
  { id: 'ecole',   label: 'École',         emoji: '🏫' },
  { id: 'famille', label: 'Famille',       emoji: '👪' },
  { id: 'garde',   label: 'Garde / nounou', emoji: '🧸' },
  { id: 'urgence', label: 'Urgence',       emoji: '🚨' },
  { id: 'autre',   label: 'Autre',         emoji: '👤' },
];
export const emojiRole = (id) => (ROLES_CONTACT.find(r => r.id === id) || ROLES_CONTACT[5]).emoji;
export const labelRole = (id) => (ROLES_CONTACT.find(r => r.id === id) || ROLES_CONTACT[5]).label;

// Types de téléphone / d'email (emoji + libellé, comme les rôles : en dur).
export const TYPES_TEL = [
  { id: 'mobile',   label: 'Mobile',   emoji: '📱' },
  { id: 'domicile', label: 'Domicile', emoji: '🏠' },
  { id: 'travail',  label: 'Travail',  emoji: '💼' },
  { id: 'autre',    label: 'Autre',    emoji: '☎️' },
];
export const TYPES_EMAIL = [
  { id: 'perso',   label: 'Perso',   emoji: '✉️' },
  { id: 'travail', label: 'Travail', emoji: '💼' },
  { id: 'autre',   label: 'Autre',   emoji: '📧' },
];
export const labelType = (types, id) => (types.find(t => t.id === id) || {}).label || '';
export const emojiType = (types, id) => (types.find(t => t.id === id) || {}).emoji || '';

// Types de SAISIE d'un champ à la carte (widget + clavier + action cliquable).
export const SAISIES_CHAMP = [
  { id: 'texte',  label: 'Texte',     emoji: '🔤', clavier: 'default',       action: null },
  { id: 'date',   label: 'Date',      emoji: '📅', clavier: 'default',       action: null },
  { id: 'tel',    label: 'Téléphone', emoji: '📞', clavier: 'phone-pad',     action: 'tel' },
  { id: 'mail',   label: 'Email',     emoji: '✉️', clavier: 'email-address', action: 'mail' },
  { id: 'lien',   label: 'Lien',      emoji: '🌐', clavier: 'url',           action: 'url' },
  { id: 'nombre', label: 'Nombre',    emoji: '🔢', clavier: 'numeric',       action: null },
  { id: 'horaire', label: 'Horaire',  emoji: '🕒', clavier: 'default',       action: null },
];
export const saisieDef = (id) => SAISIES_CHAMP.find(x => x.id === id) || SAISIES_CHAMP[0];

// Catalogue de propriétés « à la carte » (ajoutées d'un clic ; retirées si vides).
//   saisie : type de widget/clavier (cf. SAISIES_CHAMP) → date ouvre le sélecteur.
export const CHAMPS_CONTACT = [
  { cle: 'organisation', nom: 'Organisation',          emoji: '🏢', saisie: 'texte' },
  { cle: 'fonction',     nom: 'Fonction',              emoji: '💼', saisie: 'texte' },
  { cle: 'relation',     nom: 'Lien / relation',       emoji: '🫂', saisie: 'texte' },
  { cle: 'siteWeb',      nom: 'Site web',              emoji: '🌐', saisie: 'lien' },
  { cle: 'horaires',     nom: 'Horaires',              emoji: '🕒', saisie: 'horaire' },
  { cle: 'anniversaire', nom: 'Anniversaire',          emoji: '🎂', saisie: 'date' },
  { cle: 'identifiant',  nom: 'Identifiant / dossier', emoji: '🪪', saisie: 'texte' },
  { cle: 'reseau',       nom: 'Réseau social',         emoji: '💬', saisie: 'texte' },
];
// ── Horaires ────────────────────────────────────────────────────────
// Un horaire vaut pour UN jour ou pour TOUS : c'est le cas courant (« ouvert
// 9h-18h »), et distinguer les deux evite d'ecrire sept lignes identiques.
export const JOURS_HORAIRE = [
  { id: 'tous', court: 'Tous', label: 'Tous les jours' },
  { id: 'lun',  court: 'Lun',  label: 'Lundi' },
  { id: 'mar',  court: 'Mar',  label: 'Mardi' },
  { id: 'mer',  court: 'Mer',  label: 'Mercredi' },
  { id: 'jeu',  court: 'Jeu',  label: 'Jeudi' },
  { id: 'ven',  court: 'Ven',  label: 'Vendredi' },
  { id: 'sam',  court: 'Sam',  label: 'Samedi' },
  { id: 'dim',  court: 'Dim',  label: 'Dimanche' },
];
export const jourHoraire = (id) => JOURS_HORAIRE.find(j => j.id === id) || JOURS_HORAIRE[0];

// Stockage : « jour|HH:MM|HH:MM » — une chaine, donc le modele du champ est
// inchange et les anciennes valeurs texte restent lisibles (parse tolerant :
// ce qui n'est pas reconnu revient vide plutot que de corrompre l'affichage).
export function parseHoraire(v) {
  const [jour, debut, fin] = String(v || '').split('|');
  const ok = (h) => (/^\d{1,2}:\d{2}$/.test(h || '') ? h : '');
  return {
    jour: JOURS_HORAIRE.some(j => j.id === jour) ? jour : 'tous',
    debut: ok(debut), fin: ok(fin),
  };
}
export const canonHoraire = ({ jour, debut, fin }) => `${jour || 'tous'}|${debut || ''}|${fin || ''}`;

// Plusieurs plages par champ, separees par « ; ». Une valeur vide donne UNE
// plage vierge : le widget a toujours une ligne a afficher.
export function parseHoraires(v) {
  const parts = String(v || '').split(';').filter(x => x.trim());
  return parts.length ? parts.map(parseHoraire) : [parseHoraire('')];
}
export const canonHoraires = (liste) => (liste || []).map(canonHoraire).join(';');

// Rendu lisible en fiche : une plage par ligne.
//   « Tous les jours · 09:00 → 18:00 »
export function libelleHoraire(v) {
  return parseHoraires(v).map(({ jour, debut, fin }) => {
    if (!debut && !fin) return jourHoraire(jour).label;
    const plage = debut && fin ? `${debut} → ${fin}` : (debut || fin);
    return `${jourHoraire(jour).label} · ${plage}`;
  }).join('\n');
}

export const champContact = (cle) => CHAMPS_CONTACT.find(c => c.cle === cle) || null;
export const estChampPerso = (cle) => String(cle || '').startsWith('perso');

// URL cliquable d'un champ à la carte selon son type de saisie.
export function urlChamp(champ) {
  const v = (champ?.valeur || '').trim();
  if (!v) return '';
  const a = champ?.action || saisieDef(champ?.saisie).action;
  if (a === 'tel')  return `tel:${v.replace(/\s/g, '')}`;
  if (a === 'mail') return `mailto:${v}`;
  if (a === 'url')  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
  return '';
}

// Identifiant local d'une entrée (téléphone/email) — runtime app, pas Firestore.
let _c = 0;
export const nouvelIdEntree = () => `e${Date.now().toString(36)}${(_c++).toString(36)}`;

// ── Normalisation multi-valeurs (avec migration depuis l'ancien format) ──
export function normaliserTels(c) {
  if (Array.isArray(c?.telephones)) return c.telephones.filter(e => e && (e.valeur || '').trim());
  if ((c?.telephone || '').trim()) return [{ id: 't0', type: 'autre', valeur: c.telephone.trim(), principal: true }];
  return [];
}
export function normaliserEmails(c) {
  if (Array.isArray(c?.emails)) return c.emails.filter(e => e && (e.valeur || '').trim());
  if ((c?.email || '').trim()) return [{ id: 'm0', type: 'perso', valeur: c.email.trim(), principal: true }];
  return [];
}
export function normaliserLieux(c) {
  if (Array.isArray(c?.lieux)) return c.lieux.filter(e => e && e.lieuId);
  if (c?.lieuId) return [{ lieuId: c.lieuId, principal: true }];
  return [];
}
const principalDe = (arr) => (Array.isArray(arr) ? (arr.find(e => e.principal) || arr[0] || null) : null);
export const telPrincipal   = (c) => principalDe(normaliserTels(c))?.valeur || '';
export const emailPrincipal = (c) => principalDe(normaliserEmails(c))?.valeur || '';
export const lieuPrincipalId = (c) => principalDe(normaliserLieux(c))?.lieuId || '';

// Champs « à la carte » → tableau { cle, nom, emoji, saisie, valeur, action? }.
// Accepte le CONTACT (lit `.champs`) OU directement le tableau — comme les autres
// normaliser* prennent le contact ; sans ça les champs n'étaient jamais relus.
export function normaliserChamps(cOuArr) {
  const arr = Array.isArray(cOuArr) ? cOuArr : (cOuArr?.champs || []);
  return arr.map(c => ({
    cle: c.cle, nom: c.nom, emoji: c.emoji,
    saisie: c.saisie || (c.action === 'url' ? 'lien' : c.action === 'tel' ? 'tel' : c.action === 'mail' ? 'mail' : 'texte'),
    valeur: c.valeur, action: c.action || null,
  }));
}

// Recherche / tri (liste). texteRecherche agrège tout le contenu utile.
export function texteRecherche(c) {
  return [
    nomAffiche(c), c?.surnom, labelRole(c?.role),
    ...normaliserTels(c).map(e => e.valeur),
    ...normaliserEmails(c).map(e => e.valeur),
    ...normaliserChamps(c).map(e => e.valeur),
  ].filter(Boolean).join(' ').toLowerCase();
}
export function filtrerContacts(list, q) {
  const s = (q || '').trim().toLowerCase();
  return s ? list.filter(c => texteRecherche(c).includes(s)) : list;
}
export const TRIS_CONTACT = [{ id: 'nom' }, { id: 'categorie' }];
export function trierContacts(list, tri) {
  const arr = [...list];
  if (tri === 'categorie') {
    arr.sort((a, b) => labelRole(a.role).localeCompare(labelRole(b.role)) || nomAffiche(a).localeCompare(nomAffiche(b)));
  } else {
    arr.sort((a, b) => nomAffiche(a).localeCompare(nomAffiche(b)));
  }
  return arr;
}

// Nom affiché = « Prénom Nom » (les deux optionnels ; au moins un requis).
export const nomAffiche = (c) => [c?.prenom, c?.nom].filter(Boolean).join(' ').trim();

// Écoute EN DIRECT les contacts d'un calendrier (triés par nom affiché).
//
// `onErreur(message)` est remonté à l'appelant. Sans lui, un refus de règle
// répondait `callback([])` : le carnet s'affichait VIDE sans rien signaler, et
// « je n'ai pas de contacts » devenait indiscernable de « je n'ai pas le droit
// de les lire ». C'est le piège documenté depuis le correctif `estCal` du
// 06/08, et celui qui a rendu l'incident messagerie du 09/08 indiagnosticable
// depuis l'app — même correction ici (demande de Cédric, 10/08).
export function ecouterContacts(calendrierId, callback, onErreur) {
  if (!calendrierId) { callback([]); return () => {}; }
  const q = query(collection(db, 'contacts'), where('calendrierId', '==', calendrierId));
  return onSnapshot(
    q,
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => nomAffiche(a).localeCompare(nomAffiche(b)));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e?.message || String(e)); },
  );
}

// Nettoie un tableau d'entrées typées : trim, retire les vides, garantit un seul principal.
function nettoyerEntrees(arr, typeDefaut) {
  if (!Array.isArray(arr)) return [];
  const out = arr
    .map(e => ({ id: e.id || nouvelIdEntree(), type: e.type || typeDefaut, valeur: (e.valeur || '').trim(), principal: !!e.principal }))
    .filter(e => e.valeur);
  return marquerUnPrincipal(out);
}
function nettoyerLieux(arr) {
  if (!Array.isArray(arr)) return [];
  const out = arr.filter(e => e && e.lieuId).map(e => ({ lieuId: e.lieuId, principal: !!e.principal }));
  return marquerUnPrincipal(out);
}
function nettoyerChamps(arr) {
  return normaliserChamps(arr)
    .filter(c => typeof c.valeur === 'string' && c.valeur.trim())
    .map(c => {
      const action = c.action || saisieDef(c.saisie).action;
      return {
        cle: c.cle, nom: (c.nom || c.cle || '').trim() || 'Champ', emoji: c.emoji || '🏷️',
        saisie: c.saisie || 'texte', valeur: c.valeur.trim(), ...(action ? { action } : {}),
      };
    });
}
function marquerUnPrincipal(out) {
  if (out.length && !out.some(e => e.principal)) out[0].principal = true;
  let vu = false;
  out.forEach(e => { if (e.principal && !vu) vu = true; else e.principal = false; });
  return out;
}

// Champs normalisés : modèle multi-valeurs ; les anciens scalaires sont neutralisés.
function corps(c) {
  return {
    prenom: (c.prenom || '').trim(),
    nom: (c.nom || '').trim(),
    surnom: (c.surnom || '').trim(),
    role: c.role || 'autre',
    telephones: nettoyerEntrees(c.telephones, 'autre'),
    emails: nettoyerEntrees(c.emails, 'perso'),
    lieux: nettoyerLieux(c.lieux),
    champs: nettoyerChamps(c.champs),
    note: (c.note || '').trim(),
    telephone: '', email: '', lieuId: '', lieu: '', adresse: '',
  };
}

export async function ajouterContact(calendrierId, c, creePar) {
  const b = corps(c);
  if (!b.nom && !b.prenom) throw new Error('Renseigne au moins un prénom ou un nom.');
  return addDoc(collection(db, 'contacts'), {
    calendrierId, ...b, creePar: creePar || null, creeLe: new Date().toISOString(),
  });
}
export async function modifierContact(id, c) {
  await updateDoc(doc(db, 'contacts', id), corps(c));
}
export async function supprimerContact(id) {
  await deleteDoc(doc(db, 'contacts', id));
}
